import React, { createContext, useContext, useState, useEffect } from "react";

const DarkModeContext = createContext(null);

// CSS variable maps for each mode
var DARK = {
  "--page-bg":    "#030303",
  "--card-bg":    "#060606",
  "--surface-bg": "#0A0A0A",
  "--header-bg":  "rgba(3,3,3,0.85)",
  "--text-primary":   "#FFFFFF",
  "--text-secondary": "#A1A1AA",
  "--border-col": "rgba(255,255,255,0.08)",
};

var LIGHT = {
  "--page-bg":    "#FAFAFA",
  "--card-bg":    "#FFFFFF",
  "--surface-bg": "#F3F3F3",
  "--header-bg":  "rgba(250,250,250,0.90)",
  "--text-primary":   "#111111",
  "--text-secondary": "#555555",
  "--border-col": "rgba(0,0,0,0.09)",
};

export function DarkModeProvider({ children }) {
  var getSystem = function () {
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  };

  var [isDark, setIsDark] = useState(function () {
    var saved = localStorage.getItem("gl_dark_mode");
    if (saved !== null) return saved === "true";
    return getSystem();
  });

  // Apply CSS vars to :root whenever mode changes
  useEffect(function () {
    var vars = isDark ? DARK : LIGHT;
    Object.keys(vars).forEach(function (key) {
      document.documentElement.style.setProperty(key, vars[key]);
    });
    if (isDark) {
      document.documentElement.classList.add("dark-mode");
      document.documentElement.classList.remove("light-mode");
    } else {
      document.documentElement.classList.add("light-mode");
      document.documentElement.classList.remove("dark-mode");
    }
  }, [isDark]);

  // Listen for OS-level changes (only if user hasn't manually overridden)
  useEffect(function () {
    var saved = localStorage.getItem("gl_dark_mode");
    if (saved !== null) return;
    var mq      = window.matchMedia("(prefers-color-scheme: dark)");
    var handler = function (e) { setIsDark(e.matches); };
    mq.addEventListener("change", handler);
    return function () { mq.removeEventListener("change", handler); };
  }, []);

  function toggleMode() {
    setIsDark(function (prev) {
      var next = !prev;
      localStorage.setItem("gl_dark_mode", String(next));
      return next;
    });
  }

  function resetToSystem() {
    localStorage.removeItem("gl_dark_mode");
    setIsDark(getSystem());
  }

  return (
    <DarkModeContext.Provider value={{ isDark, toggleMode, resetToSystem }}>
      {children}
    </DarkModeContext.Provider>
  );
}

export function useDarkMode() {
  var ctx = useContext(DarkModeContext);
  if (!ctx) throw new Error("useDarkMode must be used within DarkModeProvider");
  return ctx;
} 
